import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import { updateOrganizationTrial, EditTrialPayload } from '../../Services/BorgAPIClient';

const END_NOW = 'endNow';
const END_NOW_PAID = 'endNowAndSetPaid';
const EXTEND_MONTHS = 'extendMonths';
const TRIAL_END_DATE = 'trialEndDate';
const DATE_TIME_LOCAL_FORMAT = 'YYYY-MM-DDTHH:mm';

type OrganizationForTrial = {
  id: string;
  trialStartDate?: string | null;
};

interface EditTrialModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  organization: OrganizationForTrial | null;
}

const EditTrialModal: React.FC<EditTrialModalProps> = ({ open, onClose, onConfirm, organization }) => {
  const [option, setOption] = useState(END_NOW);
  const [months, setMonths] = useState(3);
  const [endDate, setEndDate] = useState<Date | null>(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 3);
    return d;
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setOption(END_NOW);
      setMonths(3);
      const d = new Date();
      d.setMonth(d.getMonth() + 3);
      setEndDate(d);
      setError(null);
    }
  }, [open]);

  const handleConfirm = async () => {
    if (!organization?.id) return;
    setError(null);
    let payload: EditTrialPayload;
    if (option === END_NOW) {
      payload = { endNow: true };
    } else if (option === END_NOW_PAID) {
      payload = { endNowAndSetPaid: true };
    } else if (option === EXTEND_MONTHS) {
      if (months < 1) {
        setError('Months must be at least 1.');
        return;
      }
      payload = { extendMonths: months };
    } else {
      const dateToUse = endDate ?? new Date();
      if (dateToUse <= new Date()) {
        setError('Trial end date must be in the future.');
        return;
      }
      payload = { trialEndDate: dateToUse.toISOString() };
    }
    try {
      await updateOrganizationTrial(organization.id, payload);
      onConfirm();
    } catch (err) {
      setError('Failed to update trial.');
    }
  };

  const trialStartFormatted = organization?.trialStartDate
    ? moment(organization.trialStartDate).format('MM/DD/YYYY @ HH:mm')
    : '—';

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Edit Trial</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ paddingTop: 0, width: '340px' }}>
        <Typography variant="body1" sx={{ marginBottom: 2 }}>
          Trial Start Date: {trialStartFormatted}
        </Typography>
        <RadioGroup value={option} onChange={(e) => { setOption(e.target.value); setError(null); }}>
          <FormControlLabel value={END_NOW} control={<Radio />} label="End Now" />
          <FormControlLabel value={END_NOW_PAID} control={<Radio />} label="End Now - Set to paid" />
          <FormControlLabel value={EXTEND_MONTHS} control={<Radio />} label="Extend Duration - Months" />
          <FormControlLabel value={TRIAL_END_DATE} control={<Radio />} label="Trial End - Date" />
        </RadioGroup>
        {option === EXTEND_MONTHS && (
          <TextField
            label="Months"
            type="number"
            variant="standard"
            value={months}
            onChange={(e) => setMonths(parseInt(e.target.value, 10) || 0)}
            inputProps={{ min: 1 }}
            fullWidth
            margin="dense"
          />
        )}
        {option === TRIAL_END_DATE && (
          <TextField
            label="Schedule Trial End"
            type="datetime-local"
            variant="standard"
            value={endDate ? moment(endDate).format(DATE_TIME_LOCAL_FORMAT) : ''}
            onChange={(e) => setEndDate(moment(e.target.value).toDate())}
            InputLabelProps={{ shrink: true }}
            fullWidth
            margin="dense"
          />
        )}
        {error && (
          <Typography color="error" variant="body2" sx={{ marginTop: 1 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleConfirm}>Confirm</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditTrialModal;
